import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SearchField from "@/components/feature/SearchField";
import SectionHead from "@/components/base/SectionHead";
import WorkCard from "@/components/base/WorkCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import HotRanking from "@/pages/home/components/HotRanking";
import SpecialShowcase from "@/pages/home/components/SpecialShowcase";
import {
  allCategories,
  allSpecials,
  bannerOf,
  getWork,
  hotWorks,
  latestWorks,
} from "@/lib/content";
import type { Work } from "@/types/content";

const heroWorks = ["neon-never-sleeps", "changan-under-moon", "seventh-floor-office"]
  .map((id) => getWork(id))
  .filter((item): item is Work => Boolean(item));

const heroSlides = [
  { work: heroWorks[0], banner: bannerOf("tokyoNight"), label: "本周焦点 · 最新上线" },
  { work: heroWorks[1], banner: bannerOf("palaceNight"), label: "专题推荐 · 东方绮梦" },
  { work: heroWorks[2], banner: bannerOf("citySuite"), label: "热门榜首 · 都市情缘" },
].filter((slide) => Boolean(slide.work));

const keywords = ["夜色东京", "花魁物语", "都市夜归人", "悬疑", "古装", "伦理"];

export default function Home() {
  const latest = latestWorks(20);
  const hot = hotWorks(10);

  useEffect(() => {
    document.title = "亚洲无码 - 高清亚洲成人影视内容平台";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "亚洲无码网站专注提供丰富的亚洲成人影视内容，涵盖多种类型，用户可浏览最新作品及热门推荐，体验多样化影视资源。",
      );
    }
  }, []);

  return (
    <SiteLayout>
      <section className="border-b border-background-300/50 bg-background-100">
        <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-4 px-4 py-5 md:flex-row md:items-end md:justify-between md:px-8 md:py-6">
          <div className="min-w-0">
            <p className="font-heading text-[10px] uppercase tracking-[0.3em] text-primary-500">
              Asian Uncensored Archive
            </p>
            <h1 className="mt-1.5 font-heading text-lg font-bold leading-snug text-foreground-50 md:text-2xl">
              亚洲无码 · 高清亚洲成人影视内容平台
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-foreground-500">
            <span>
              <strong className="font-heading text-base text-primary-400">50</strong> 部作品
            </span>
            <span>
              <strong className="font-heading text-base text-primary-400">5</strong> 大分类
            </span>
            <span>
              <strong className="font-heading text-base text-primary-400">3</strong> 个专题
            </span>
            <span className="hidden items-center gap-1 rounded border border-accent-600/50 px-2 py-0.5 text-accent-300 sm:flex">
              <i className="ri-shield-line" />
              资料索引 · 不含播放
            </span>
          </div>
        </div>
      </section>

      <HeroCarousel slides={heroSlides} />

      <div className="relative z-10 mx-auto -mt-10 w-full max-w-[1440px] px-4 md:-mt-12 md:px-8">
        <div className="rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex flex-shrink-0 items-center gap-2 pr-2 md:border-r md:border-background-300/70">
              <span className="flex h-8 w-8 items-center justify-center rounded bg-primary-500/15 text-primary-400">
                <i className="ri-search-eye-line" />
              </span>
              <div>
                <p className="font-heading text-[12px] font-semibold text-foreground-100">作品检索</p>
                <p className="text-[10px] text-foreground-600">支持片名 / 类型 / 导演</p>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <SearchField size="lg" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-300/50 pt-3">
            <span className="text-[11px] text-foreground-600">热门：</span>
            {keywords.map((word) => (
              <Link
                key={word}
                to={`/category/all?keyword=${encodeURIComponent(word)}`}
                className="cursor-pointer rounded-full border border-background-300/60 px-3 py-1 text-[11px] text-foreground-400 transition hover:border-primary-700/70 hover:text-primary-300"
              >
                {word}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <SectionHead
          eyebrow="Categories"
          title="分类快捷入口"
          desc="按题材进入对应片库，五类内容各自独立编目，可继续按地区与年份筛选。"
          actionLabel="全部分类"
          actionTo="/category/all"
        />
        <CategoryGrid categories={allCategories} />
      </section>

      <section className="border-y border-background-300/50 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
          <SectionHead
            eyebrow="Latest Releases"
            title="最新上线作品"
            desc="按上线年份与热度综合排序，展示近期收录的 20 部高清成人影视作品资料。"
            actionLabel="查看全部作品"
            actionTo="/category/all"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
            {latest.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <SectionHead
          eyebrow="Trending Now"
          title="热门推荐排行"
          desc="依据站内浏览量模拟排序，可作为挑选作品时的参考。"
          actionLabel="按热门排序浏览"
          actionTo="/category/all?sort=hot"
        />
        <div className="rounded-lg border border-background-300/50 bg-background-100/60 p-2 md:p-4">
          <HotRanking works={hot} />
        </div>
      </section>

      <section className="border-y border-background-300/50 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
          <SectionHead
            eyebrow="Editorial Collections"
            title="精选专题合集"
            desc="由编辑组按主题挑选的作品集合，适合按氛围与题材一次性浏览。"
            actionLabel="浏览全部作品"
            actionTo="/category/all"
          />
          <SpecialShowcase specials={allSpecials} />
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="relative overflow-hidden rounded-lg border border-background-300/60 bg-background-100 px-5 py-8 md:px-10 md:py-10">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary-500/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-accent-500/10 blur-2xl" />
          <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="font-heading text-[10px] uppercase tracking-[0.28em] text-primary-500">Notice</p>
              <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
                这是一个成人影视资料索引站
              </h2>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-500">
                本站仅整理作品信息、分类标签与剧集目录，不提供任何在线播放、下载、支付或会员服务。请确认您已满 18 岁，并在所在地法律允许的范围内访问。
              </p>
            </div>
            <Link
              to="/about"
              className="flex w-fit cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition hover:bg-primary-400"
            >
              阅读访问须知
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}